/**
 * vendor/isomorphic/packages/isomorphic-core/src/components/cards/metric-card.tsx — the template's
 * own `MetricCard` (icon, title, metric, info, children), composed rather than redrawn, with the
 * Operations cards' shared treatment layered on through its class props:
 *
 *  - a 4px severity stripe down the leading edge (`TONE_STRIPE`), so a row of eight reads by state
 *    before a single number is;
 *  - the status in words in a pill (`TONE_STATUS`) — SC-009: colour never carries severity alone;
 *  - the number large, the caption quiet and clamped to two lines so every card in a row lines up;
 *  - the whole card is the link (the footer's `after:inset-0`), a target the size of the card rather
 *    than a line of blue text, with the destination still named in words in the footer.
 *
 * One file, so eight tiles cannot drift apart. The value shown is whatever the tile passes — always
 * the API's own figure (constitution I); nothing here computes.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { MetricCard, cn } from "@steelyard/theme";
import { Text } from "rizzui";
import { TONE_STATUS, TONE_STRIPE, problemMessage, toneIconClass, toneMetricClass, type Tone } from "./tone";

export function StatusCard({
  title,
  icon,
  tone,
  value,
  word = false,
  caption,
  link,
}: {
  title: string;
  icon: ReactNode;
  tone: Tone;
  /** The API's figure, or a state in words ("Needs attention", "Unreadable"). */
  value: ReactNode;
  /** The value is already a word: set smaller, and no pill — it would only repeat it. */
  word?: boolean;
  caption: ReactNode;
  link?: { to: string; label: string };
}) {
  const status = TONE_STATUS[tone];
  return (
    <MetricCard
      title={title}
      icon={icon}
      iconClassName={cn(toneIconClass(tone), "shrink-0 rounded-xl")}
      // Room for the pill beside the title only; the caption below runs the card's full width.
      titleClassName={cn("text-sm font-medium text-gray-600", word ? undefined : "pe-24")}
      metric={<span className={cn(toneMetricClass(tone), word ? "text-lg" : "text-[28px] leading-9")}>{value}</span>}
      metricClassName="mt-0.5"
      info={<Text className="mt-1.5 line-clamp-2 text-xs leading-relaxed text-gray-500">{caption}</Text>}
      className={cn(
        "relative flex h-full flex-col overflow-hidden ps-6 transition-shadow duration-200 before:absolute before:inset-y-0 before:start-0 before:w-1 hover:shadow-md has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/50 [&>div:first-child]:items-start",
        TONE_STRIPE[tone],
      )}
    >
      {word ? null : (
        <span className={cn("absolute end-4 top-4 rounded-full px-2.5 py-0.5 text-xs font-medium", status.className)}>{status.label}</span>
      )}
      {link ? (
        <div className="mt-auto pt-4">
          <Link
            to={link.to}
            className="flex border-t border-muted pt-3 text-sm font-medium text-primary after:absolute after:inset-0 hover:text-primary-dark hover:underline focus-visible:outline-none"
          >
            {link.label}
          </Link>
        </div>
      ) : null}
    </MetricCard>
  );
}

/** The failed-read arm every Operations card shares: "Unreadable" in words, the API's reason beneath. */
export function UnreadableStatusCard({ title, icon, error, fallback }: { title: string; icon: ReactNode; error: unknown; fallback: string }) {
  return <StatusCard title={title} icon={icon} tone="destructive" value="Unreadable" word caption={problemMessage(error, fallback)} />;
}
